import React from "react";
import { Tabs, Radio } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import styles from "./EditContent.module.scss";
import "./CustomStyle.css";
import Button from "@components/button/Button";
import { TextInput, TextInputValue } from "@components/text-input/TextInput";
import { FeedbackFormProvider } from "./feedback-form-editor/useFeddbackFormEdit";
import GenericContent from "./GenericContent";
import AddGroupModal from "./AddGroupModal";
import Spinner from "@components/spinner/Spinner";
import { useEditContent } from "./useEditContent";
import { ContentType, UnitTypes, ExtendedContent } from "@src/types";
import { useEditUnit } from "../useEditUnit";
const { TabPane } = Tabs;

function EditContentComponent() {
  const {
    isGroupsLoading,
    availableGroupOptions,
    getUrl,
    clearFile,
    handleSave,
    handleRemoveModule,
    handleRemoveGroup,
    handleAddGroup,
    onChangeTab,
    onAddGroup,
    errors,
    formData,
    fileUploadingPercent,
    setFileUploadingPercent,
    activeTab,
    isOpenAddModal,
    setErrors,
    fileLoading,
    loading,
    groups,
    name,
    description,
    setName,
    setDescription,
    onCloseAddGroupModal,
    data,
    setData,
    dataInputRef,
    isRemoving,
    uploadVideoAndSave,
    isGroupRemoving,
    isDisabled,
    handleContentDataSave,
    deleteGroupModuleData,
    deleteModuleData,
    onChangeRequiredLesson,
    onChangeLastLessonMark,
    onChangeAdditionalAfter,
    isAdditionalAfter,
    isRequired,
    isLastLesson,
    isClassPresentation,
    fileUploadFinished,
    isGroupContent,
    handleUnitTypeChange,
    currentUnitType,
    onChangeHomeEdUseDefaultState,
    homeEdState,
    isPreparation,
    currentContent,
    handleChangeVideo,
  } = useEditContent();

  const { heaUnit, selectionLevel } = useEditUnit();

  return (
    <>
      <div className={styles.row}>
        <div className={styles.actions}>
          <div className={styles.title}>Edit contents</div>
          <div className={styles.buttons}>
            <Button
              color="white"
              isLoading={loading}
              disabled={isDisabled}
              onClick={handleSave}
              size={"small"}
              label={"SAVE"}
              leftIcon={
                loading ? <Spinner width={20} height={20} /> : undefined
              }
            />
            <Button
              color="red"
              onClick={handleRemoveModule}
              outline={true}
              size={"small"}
              disabled={isDisabled}
              leftIcon={
                isRemoving ? <Spinner width={20} height={20} /> : undefined
              }
              label={"DELETE"}
            />
          </div>
        </div>
      </div>

      {isPreparation ||
      !heaUnit ||
      selectionLevel === "SectionContent" ? null : (
        <div className={styles.buttonsRowContainer}>
          <Radio.Group
            onChange={handleUnitTypeChange(currentContent?.id)}
            value={currentUnitType}
            optionType="button"
            buttonStyle="solid"
            disabled={isGroupsLoading}
          >
            <Radio.Button
              style={{
                width: 100,
                height: 40,
                lineHeight: "40px",
                textAlign: "center",
              }}
              value={UnitTypes.School}
              autoFocus
            >
              {UnitTypes.School}
            </Radio.Button>
            <Radio.Button
              style={{
                width: 100,
                height: 40,
                lineHeight: "40px",
                textAlign: "center",
              }}
              value={UnitTypes.HomeEd}
            >
              {UnitTypes.HomeEd}
            </Radio.Button>
          </Radio.Group>
        </div>
      )}

      <div className={styles.row}>
        <TextInput
          hasError={!!errors.name}
          subTitle={errors.name}
          label={"Name"}
          id={"name"}
          placeholder="Name"
          value={name === "Content name" ? "" : name}
          onChange={(value: TextInputValue) => {
            setName(`${value}`);
          }}
          readonly={groups?.[0]?.type === ContentType.PARENT}
        />
      </div>
      {groups[0]?.type !== ContentType.PARENT ? (
        <Tabs
          defaultActiveKey={groups[0]?.groupName || ""}
          activeKey={activeTab}
          onChange={onChangeTab}
          className={styles.tabsContainer}
          tabBarExtraContent={
            <div className={styles.tabBarExtraContent}>
              {isGroupsLoading ? (
                <Spinner width={20} height={20} />
              ) : (
                <div>
                  {isDisabled ? null : (
                    <Button
                      label={""}
                      onClick={handleAddGroup}
                      className={styles.AddGroupButton}
                      outline
                      size={"small"}
                      leftIcon={
                        <PlusOutlined style={{ fontWeight: "bolder" }} />
                      }
                    />
                  )}
                </div>
              )}
            </div>
          }
        >
          {groups.map((group: ExtendedContent, index: number) => (
            <TabPane
              tab={
                <p
                  className={`${styles.tabTitle} ${
                    activeTab === group.groupName ? styles.activeTab : ""
                  }`}
                >
                  {group.groupName || ""}
                </p>
              }
              key={group.groupName}
              className={styles.tabContainer}
              closable={false}
              forceRender={group.type !== ContentType.VIDEO}
              disabled={isDisabled}
            >
              <GenericContent
                errors={errors}
                setErrors={setErrors}
                fileLoading={fileLoading}
                getUrl={getUrl}
                clearFile={clearFile}
                fileUploadingPercent={fileUploadingPercent}
                setFileUploadingPercent={setFileUploadingPercent}
                content={group}
                formData={formData}
                description={description || ""}
                setDescription={setDescription}
                data={data}
                setData={setData}
                dataInputRef={dataInputRef}
                isDefault={!isGroupContent && index !== undefined}
                handleRemoveGroup={handleRemoveGroup}
                uploadVideoAndSave={uploadVideoAndSave}
                handleChangeVideo={handleChangeVideo}
                loading={loading}
                isGroupRemoving={isGroupRemoving}
                isDisabled={isDisabled}
                handleContentDataSave={handleContentDataSave}
                deleteGroupModuleData={deleteGroupModuleData}
                deleteModuleData={deleteModuleData}
                onChangeRequiredLesson={onChangeRequiredLesson}
                onChangeLastLessonMark={onChangeLastLessonMark}
                onChangeAdditionalAfter={onChangeAdditionalAfter}
                isRequired={isRequired}
                isLastLesson={isLastLesson}
                isAdditionalAfter={isAdditionalAfter}
                isClassPresentation={isClassPresentation}
                fileUploadFinished={fileUploadFinished}
                onChangeHomeEdUseDefaultState={onChangeHomeEdUseDefaultState}
                homeEdState={homeEdState}
                currentUnitType={currentUnitType}
                isPreparation={isPreparation}
              />
            </TabPane>
          ))}
        </Tabs>
      ) : null}
      {isOpenAddModal ? (
        <AddGroupModal
          open={isOpenAddModal}
          onClose={onCloseAddGroupModal}
          groupOptions={availableGroupOptions}
          onAddGroup={onAddGroup}
          currentUnitType={currentUnitType}
        />
      ) : null}
    </>
  );
}

const EditContent = () => {
  return (
    <FeedbackFormProvider>
      <EditContentComponent />
    </FeedbackFormProvider>
  );
};

export default EditContent;
